// Physische Knopf-/Ton-Tabelle der Steirischen Harmonika (GCFB-Stimmung). EINZIGE Quelle –
// geteilt von der Griff-Visualisierung (Inspector) UND dem Keyboard-Simulator
// (`webapp/src/features/keyboard/`; nutzt zusätzlich DEFAULT_KEY_MAPPINGS/STORAGE_KEY unten).
//
// WICHTIG: Diese Tabelle bestimmt das VISUELLE Layout der Griff-Visualisierung. Das Highlight
// erfolgt POSITIONSBASIERT über (row, number) aus dem Backend (Quelle der Wahrheit ist die
// Python-Engine). Reihen-/Knopf-Nummerierung ist mit der Engine abgeglichen
// (tests/test_button_table_alignment.py: 88/92 exakt, 4 kosmetische Label-Divergenzen an den
// C#/G#-Orientierungstönen von Reihe 1). Bei Änderungen hier den Test nachziehen.

import type {BassLayoutSettings} from '@/generated'
import type {BellowsDirection, InstrumentTuning, PrecalcButton, PrecalcRow, RowData} from './harmonika.types'
import {parseNoteStr, transposedNoteLabel} from './noteParse'

export const RAW_DATA: RowData[] = [
  {
    rowId: 1,
    buttons: [
      {druck: ['C#2'], zug: ['C#2']},
      {druck: ['G#2'], zug: ['G#2']},
      {druck: ['D0'], zug: ['F#0', 'G-0']},
      {druck: ['G0'], zug: ['A0', 'B--0']},
      {druck: ['B0'], zug: ['C0']},
      {druck: ['D1'], zug: ['E1', 'F-1']},
      {druck: ['G1'], zug: ['F#1', 'G-1']},
      {druck: ['B1'], zug: ['A1', 'B--1']},
      {druck: ['D2'], zug: ['C2', 'B#1']},
      {druck: ['G2'], zug: ['E2', 'F-2']},
      {druck: ['B2'], zug: ['G-2', 'F#2']},
      {druck: ['D3'], zug: ['A3', 'B--3']},
      {druck: ['G3'], zug: ['C3']},
    ],
  },
  {
    rowId: 2,
    buttons: [
      {druck: ['C0'], zug: ['F0']},
      {druck: ['E0', 'F-0'], zug: ['G0']},
      {druck: ['G0'], zug: ['B0']},
      {druck: ['C1'], zug: ['D1']},
      {druck: ['E1', 'F-1'], zug: ['F1']},
      {druck: ['G1'], zug: ['G1']},
      {druck: ['C2'], zug: ['B1']},
      {druck: ['E2', 'F-2'], zug: ['D2']},
      {druck: ['G2'], zug: ['F2']},
      {druck: ['C3'], zug: ['A2', 'B--2']},
      {druck: ['E3', 'F-3'], zug: ['B2']},
      {druck: ['G3'], zug: ['D3']},
    ],
  },
  {
    rowId: 3,
    buttons: [
      {druck: ['F0'], zug: ['B-0', 'A#0']},
      {druck: ['A0', 'B--0'], zug: ['C1']},
      {druck: ['C1'], zug: ['E1']},
      {druck: ['F1'], zug: ['G1']},
      {druck: ['A1', 'B--1'], zug: ['B-1', 'A#1']},
      {druck: ['C2'], zug: ['C2']},
      {druck: ['F2'], zug: ['E2']},
      {druck: ['A2', 'B--2'], zug: ['G2']},
      {druck: ['C3'], zug: ['B-2', 'A#2']},
      {druck: ['F3'], zug: ['D3']},
      {druck: ['A3', 'B--3'], zug: ['E3']},
    ],
  },
  {
    rowId: 4,
    buttons: [
      {druck: ['B-0', 'A#0'], zug: ['E-1', 'D#1']},
      {druck: ['D1'], zug: ['F1']},
      {druck: ['F1'], zug: ['A1', 'A--1']},
      {druck: ['B-1', 'A#1'], zug: ['C2']},
      {druck: ['D2'], zug: ['E-2', 'D#2']},
      {druck: ['F2'], zug: ['F2']},
      {druck: ['B-2', 'A#2'], zug: ['A2', 'B--2']},
      {druck: ['D3'], zug: ['C3']},
      {druck: ['F3'], zug: ['E-3', 'D#3']},
      {druck: ['B-3', 'A#3'], zug: ['G3']},
    ],
  },
]

// R1-1/R1-2, die C♯/G♯-Orientierungstöne: die Engine führt sie in JEDER Oktave (C♯3–C♯7,
// G♯3–G♯7), diese Tabelle in einer festen – die bekannte Abweichung in
// `tests/test_button_table_alignment.py` (`_KNOWN_LABEL_DIVERGENCES`). Eine Oktave in der
// Beschriftung wäre dort geraten und widerspräche je nach Quelle (Tabelle oder gespielte Note).
const OCTAVE_FREE_BUTTON_IDS = new Set(['0-0', '0-1'])

/** Beschriftung ohne Oktave an den Knöpfen, für die die Daten keine Oktave festlegen. */
export function withoutAmbiguousOctave(rowIdx: number, btnIdx: number, label: string): string {
  return OCTAVE_FREE_BUTTON_IDS.has(`${rowIdx}-${btnIdx}`) ? label.replace(/-?\d+$/, '') : label
}

/** Vorberechnete MIDI-Werte je Knopf (kein Parsen zur Render-Zeit). */
export const PRECALC_DATA: PrecalcRow[] = RAW_DATA.map((row) => ({
  ...row,
  buttons: row.buttons.map((button) => ({
    ...button,
    druckMidis: button.druck.map(parseNoteStr).filter((n): n is number => n !== null),
    zugMidis: button.zug.map(parseNoteStr).filter((n): n is number => n !== null),
  })),
}))

/** Die MIDI-Töne eines Knopfs in einer Balgrichtung. */
export function buttonMidis(button: PrecalcButton, direction: BellowsDirection): number[] {
  return direction === 'DRUCK' ? button.druckMidis : button.zugMidis
}

/** Beschriftung eines Knopfs: erster Ton der Balgrichtung, um `offset` Halbtöne verschoben (`transposedNoteLabel`). */
export function buttonNoteLabel(rowIdx: number, btnIdx: number, direction: BellowsDirection, offset: number): string {
  const button = RAW_DATA[rowIdx]?.buttons[btnIdx]
  if (!button) return ''
  return transposedNoteLabel((direction === 'DRUCK' ? button.druck : button.zug)[0] ?? '', offset)
}

/**
 * localStorage: hochkant ist die Darstellung umgedreht, das untere Ende des Instruments steht oben
 * (`InstrumentBody`, `BassBoard`). Ein Key für alle Instanzen – `useLocalStorage` gleicher Keys
 * synchronisiert sich im selben Fenster.
 */
export const BOARDS_FLIPPED_STORAGE_KEY = 'griffschrift:boardsFlipped'

// --- Nur vom Keyboard-Simulator (`features/keyboard`) genutzt -------------------------------------
// Physische Standard-Tastenbelegung je Knopf (physische `event.code`s → layout-unabhängig über
// QWERTY/QWERTZ/AZERTY) und der localStorage-Key der Belegung. Für die reine Griff-Visualisierung
// irrelevant, liegt aber hier, damit die Knopf-/Tabellen-Daten EINE Quelle haben.

/** Standard-Tastenbelegung (physische Key-Codes) je Reihe/Knopf. */
export const DEFAULT_KEY_MAPPINGS: string[][] = [
  ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9', 'Digit0', 'Minus', 'Equal', 'Backspace'],
  ['KeyQ', 'KeyW', 'KeyE', 'KeyR', 'KeyT', 'KeyY', 'KeyU', 'KeyI', 'KeyO', 'KeyP', 'BracketLeft', 'BracketRight'],
  ['KeyA', 'KeyS', 'KeyD', 'KeyF', 'KeyG', 'KeyH', 'KeyJ', 'KeyK', 'KeyL', 'Semicolon', 'Quote'],
  ['KeyZ', 'KeyX', 'KeyC', 'KeyV', 'KeyB', 'KeyN', 'KeyM', 'Comma', 'Period', 'Slash'],
]

export const STORAGE_KEY = 'steirische_key_mappings_v2'

// --- Stimmungen -----------------------------------------------------------------------------
// Die Knopftabelle oben ist G-C-F-B. Andere Stimmungen haben dieselbe Anordnung, nur um einen
// festen Abstand verschoben – Griffschrift ist deshalb stimmungsunabhängig. Reihenfolge im
// Quartenzirkel wie beim Quetschnzirkel.
// shortcut: die Oktavlage je Stimmung ist als nächstliegender Offset −6..+5 geschätzt; mit
// Herstellerangaben je Stimmung korrigieren, falls der Simulator zu hoch oder zu tief klingt.
export const INSTRUMENT_TUNINGS: readonly InstrumentTuning[] = [
  {id: 'G-C-F-B', minorLabel: '(e-a-d-g)', offset: 0},
  {id: 'C-F-B-E♭', minorLabel: '(a-d-g-c)', offset: 5},
  {id: 'F-B-E♭-A♭', minorLabel: '(d-g-c-f)', offset: -2},
  {id: 'B-E♭-A♭-D♭', minorLabel: '(g-c-f-b)', offset: 3},
  {id: 'E♭-A♭-D♭-G♭', minorLabel: '(c-f-b-e♭)', offset: -4},
  {id: 'A♭-D♭-G♭-H', minorLabel: '(f-b-e♭-a♭)', offset: 1},
  {id: 'D♭-G♭-H-E', minorLabel: '(b-e♭-a♭-d♭)', offset: -6},
  {id: 'F♯-H-E-A', minorLabel: '(d♯-g♯-c♯-f♯)', offset: -1},
  {id: 'H-E-A-D', minorLabel: '(g♯-c♯-f♯-h)', offset: 4},
  {id: 'E-A-D-G', minorLabel: '(c♯-f♯-h-e)', offset: -3},
  {id: 'A-D-G-C', minorLabel: '(f♯-h-e-a)', offset: 2},
  {id: 'D-G-C-F', minorLabel: '(h-e-a-d)', offset: -5},
]

/** Die Stimmung zum Schlüssel; unbekannte (z. B. veraltete gespeicherte) fallen auf G-C-F-B. */
export function instrumentTuning(id: string | null | undefined): InstrumentTuning {
  return INSTRUMENT_TUNINGS.find((tuning) => tuning.id === id) ?? INSTRUMENT_TUNINGS[0]!
}

// --- Bass-/Begleitseite ---------------------------------------------------------------------
// Spiegel von `griffschrift/mapping/bass_layout.py::default_bass_layout()`. Die Silbe ist KEIN
// Tonname, sondern der Name eines Knopfes (Gross = Grundbass, Klein = Akkordbass); `druck`/`zug`
// sagen, was der Knopf in der jeweiligen Balgrichtung erzeugt. Weil Knopfzahl und vor allem die
// Belegung der inneren Reihe bauartabhaengig sind, ist das hier nur der Startwert - der Nutzer
// konfiguriert sein Instrument im Harmonika-Tab, und der Wert reist in `ConversionSettings`.
//
// DAS OBJEKTLITERAL MUSS GUELTIGES JSON BLEIBEN (doppelte Anfuehrungszeichen, kein Kommentar und
// kein haengendes Komma darin): `tests/test_bass_layout_alignment.py` liest es woertlich aus dieser
// Datei und vergleicht es mit dem Python-Default. Ein Formatwechsel bricht den Abgleich.
const DEFAULT_BASS_LAYOUT_JSON = {
  "name": "Pauli 4-reihig (GCFB)",
  "rows": [
    {
      "name": "äußere",
      "buttons": [
        {"griffschrift": "D", "kind": "bass", "druck": "B-", "zug": "F"},
        {"griffschrift": "d", "kind": "chord", "druck": "B-", "zug": "F"},
        {"griffschrift": "C", "kind": "bass", "druck": "F", "zug": "C"},
        {"griffschrift": "c", "kind": "chord", "druck": "F", "zug": "C"},
        {"griffschrift": "B", "kind": "bass", "druck": "C", "zug": "G"},
        {"griffschrift": "b", "kind": "chord", "druck": "C", "zug": "G"},
        {"griffschrift": "A", "kind": "bass", "druck": "G", "zug": "D"},
        {"griffschrift": "a", "kind": "chord", "druck": "G", "zug": "D"},
        {"griffschrift": "X", "kind": "bass", "druck": "D", "zug": "A"}
      ]
    },
    {
      "name": "innere",
      "buttons": [
        {"griffschrift": "D²", "kind": "bass", "druck": "D", "zug": "E-"},
        {"griffschrift": "d²", "kind": "chord", "druck": "Dm", "zug": "E-"},
        {"griffschrift": "C²", "kind": "bass", "druck": "A", "zug": "B-"},
        {"griffschrift": "c²", "kind": "chord", "druck": "A", "zug": "B-"},
        {"griffschrift": "B²", "kind": "bass", "druck": "E", "zug": "A"},
        {"griffschrift": "b²", "kind": "chord", "druck": "E", "zug": "Am"},
        {"griffschrift": "A²", "kind": "bass", "druck": "B", "zug": "B"},
        {"griffschrift": "a²", "kind": "chord", "druck": "B", "zug": "Bm"}
      ]
    }
  ]
}

// Der generierte Client gibt `kind` als TS-`enum` aus, das Literal oben haelt denselben String.
// Genau EINE Konvertierung an dieser Stelle – 17 Casts im Literat wuerden dessen JSON-Gueltigkeit
// und damit den Abgleichstest zerstoeren.
export const DEFAULT_BASS_LAYOUT = DEFAULT_BASS_LAYOUT_JSON as unknown as BassLayoutSettings

/** Tiefe Kopie – Presets und Default dürfen nie als geteilte Referenz in den Store wandern. */
export function cloneBassLayout(layout: BassLayoutSettings): BassLayoutSettings {
  return JSON.parse(JSON.stringify(layout)) as BassLayoutSettings
}

/** Benennt die Knöpfe um, ohne die Belegung anzufassen (Rosenzopf/Michlbauer vs. Pauli). */
function renameButtons(
  layout: BassLayoutSettings,
  name: string,
  renames: Record<string, string>,
): BassLayoutSettings {
  const next = cloneBassLayout(layout)
  next.name = name
  for (const row of next.rows ?? []) {
    for (const button of row.buttons ?? []) {
      button.griffschrift = renames[button.griffschrift] ?? button.griffschrift
    }
  }
  return next
}

/** Dieselbe Belegung ohne die 4. Reihe (dreireihige Harmonika, GCF). */
function withoutFourthRow(layout: BassLayoutSettings, name: string): BassLayoutSettings {
  const next = cloneBassLayout(layout)
  next.name = name
  for (const row of next.rows ?? []) {
    row.buttons = (row.buttons ?? []).filter(
      (button) => button.griffschrift !== 'D' && button.griffschrift !== 'd'
        && button.griffschrift !== 'D²' && button.griffschrift !== 'd²',
    )
  }
  return next
}

// Die Presets unterscheiden sich NUR in Benennung bzw. Knopfzahl, nie in der Belegung – die
// äußere Reihe ist laut Quellenlage für alle Bauarten gleich aufgebaut.
export const BASS_LAYOUT_PRESETS: readonly BassLayoutSettings[] = [
  DEFAULT_BASS_LAYOUT,
  withoutFourthRow(DEFAULT_BASS_LAYOUT, 'Pauli 3-reihig (GCF)'),
  renameButtons(DEFAULT_BASS_LAYOUT, 'Michlbauer 4-reihig (GCFB)', {
    'D²': 'E', 'd²': 'e', 'C²': 'F', 'c²': 'f', 'B²': 'G', 'b²': 'g', 'A²': 'H', 'a²': 'h',
  }),
]
